import {Component, HostBinding, OnDestroy, OnInit, ViewChild} from '@angular/core';

import { Subscription } from 'rxjs';
import { FooterButtonOption, IncomeStatementType, PageNumbers } from 'src/app/model/Enums';
import { Report, FieldValue, IncomeStatement } from 'src/app/model';
import { ReportProvider } from 'src/app/providers';
import { ReportService, ToasterService } from 'src/app/services';
import { Router } from '@angular/router';
import { ReportPageInterface } from 'src/app/interface';
import { NgForm } from '@angular/forms';
import { ReportPageConstant } from 'src/app/constants/reportPage.constants';
import {routeTransition} from '../../../../../animations/route-transition';

@Component({
  selector: 'app-manager-salary',
  templateUrl: './manager-salary.component.html',
  styleUrls: ['./manager-salary.component.scss'],
  animations: [routeTransition],
})
export class ManagerSalaryComponent implements OnInit, OnDestroy, ReportPageInterface {
  @HostBinding('@routeTransition') routeAnimation = true;
  @ViewChild('managerSalaryForm') managerSalaryForm: NgForm;

  public currentReport: Report;
  private reportSub: Subscription;
  reportPageConst = ReportPageConstant;

  incomeYearList: IncomeStatement[] = [];
  isManual: boolean = true;
  common_GM_SALARY: string = null;
  common_GM_SALARY_TAX: boolean = false;

  constructor(private reportService: ReportService,
    private reportProvider: ReportProvider,
    private router: Router,
    private toastr: ToasterService) { }

  ngOnInit(): void {
    this.reportSub = this.reportService.report$.subscribe(currentReport => {
      this.currentReport = currentReport;
      if (this.currentReport && this.currentReport.LATEST) {
        if (this.currentReport.LATEST.INCOME_STATEMENT.length) {
          for (let i = 0; i < this.currentReport.LATEST.INCOME_STATEMENT.length; i++) {
            const element = this.currentReport.LATEST.INCOME_STATEMENT[i];
            if (element.IS_TYPE != IncomeStatementType.INTERIM) {
              element.GM_SALARY = Number(element.GM_SALARY) < 0 ? null : element.GM_SALARY;
              this.incomeYearList.push(element);
            }
          }
          this.incomeYearList.sort((a, b) => Number(a.YEAR) - Number(b.YEAR));
          this.checkIsManual();
        }
        if (this.currentReport.LATEST.ASSISTED_GM_SALARY) {
          this.getAutoCalculatedGmSalary();
        }
      }
    });
  }

  ngOnDestroy(): void {
    if (this.currentReport) {
      this.incomeYearList.forEach(element => {
        const incomeStatementIndex = this.currentReport.LATEST.INCOME_STATEMENT.findIndex(el => el.ID === element.ID);
        if (incomeStatementIndex != -1) {
          this.currentReport.LATEST.INCOME_STATEMENT[incomeStatementIndex] = element
        }
      });
    }
    if (this.reportSub)
      this.reportSub.unsubscribe();
  }

  /**
   * @description field update API
   * @param fieldName field name which we want to update
   * @param obj income statement which year we want to update
   */
  callUpdateAPI(fieldName: string, obj: IncomeStatement) {
    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'INCOME_STATEMENT';
    objFieldValue.FIELD = fieldName;
    objFieldValue.VALUE = obj[fieldName];
    this.reportProvider.updateReportField(obj.ID, objFieldValue).subscribe(res => {
      if (!res.success) {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  callVersionDetailsUpdateAPI(fieldName: string) {
    let objFieldValue = new FieldValue();
    objFieldValue.GROUP = 'REPORT_VERSION';
    objFieldValue.FIELD = fieldName;
    objFieldValue.VALUE = this.currentReport.LATEST[fieldName];
    this.reportProvider.updateReportField(this.currentReport.LATEST.ID, objFieldValue).subscribe(res => {
      if (res.success) {
        if (fieldName === 'ASSISTED_GM_SALARY' && this.currentReport.LATEST.ASSISTED_GM_SALARY) {
          this.getAutoCalculatedGmSalary();
        }
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description save data for all
   * @param fieldName field name
   */
  async saveCommonData(fieldName: string) {
    for (let i = 0; i < this.incomeYearList.length; i++) {
      this.incomeYearList[i][fieldName] = fieldName == 'GM_SALARY' ? this.common_GM_SALARY : this.common_GM_SALARY_TAX;
      await this.callUpdateSequential(fieldName, this.incomeYearList[i]).then();
    }
  }

  callUpdateSequential(fieldName: string, obj: IncomeStatement) {
    return new Promise<void>(resolve => {
      let objFieldValue = new FieldValue();
      objFieldValue.GROUP = 'INCOME_STATEMENT';
      objFieldValue.FIELD = fieldName;
      objFieldValue.VALUE = obj[fieldName];
      this.reportProvider.updateReportField(obj.ID, objFieldValue).subscribe(res => {
        if (!res.success) {
          this.toastr.error(res.error.txt);
        }
        resolve();
      }, err => {
        // Error Handling
      });
    });
  }

  /**
   * @description check is manual entry or not
   */
  checkIsManual() {
    const checkSame = this.incomeYearList.some((item) => item.GM_SALARY != this.incomeYearList[0].GM_SALARY || item.GM_SALARY_TAX != this.incomeYearList[0].GM_SALARY_TAX);
    if (!checkSame) {
      this.isManual = false;
      this.common_GM_SALARY = Number(this.incomeYearList[0].GM_SALARY) < 0 || this.incomeYearList[0].GM_SALARY == null ? null : Number(this.incomeYearList[0].GM_SALARY).toString();
      this.common_GM_SALARY_TAX = this.incomeYearList[0].GM_SALARY_TAX;
    }
  }

  /**
   * @description for check validation for all owners
   * @returns valid or not
   */
  checkManagerSalary() {
    return this.incomeYearList.some(el => !el.GM_SALARY || !this.currentReport.LATEST.UNNAMED_SALARY);
  }

  /**
   * @description goto ownership page
   */
  gotoOwnership() {
    this.reportService.updateMaxStage(PageNumbers.MANAGER_SALARY);
    this.router.navigateByUrl(`/report/${this.currentReport.ID}/ownership`);
  }

  getAutoCalculatedGmSalary() {
    this.reportProvider.getGeneralManagerAutoCalcValue(this.currentReport.LATEST.ID).subscribe(res => {
      if (res.success) {
        this.currentReport.LATEST.UNNAMED_SALARY = res.result
      } else {
        this.toastr.error(res.error.txt);
      }
    }, err => {
      // Error Handling
    });
  }

  /**
   * @description function of interface for validation
   * @returns form valid or not
   */
  isFormValid(): boolean {
    return (this.managerSalaryForm.valid || this.currentReport.isLocked);
  }

  /**
   * @description function of interface for skip functionality
   * @returns form can be skippable or not
   */
  canBeSkipped(): boolean {
    return false;
  }

  /**
   * @description report footer button click options
   * @param buttonOption which button clicked by user
   */
  footerButtonClick(buttonOption: number) {
    if (buttonOption == FooterButtonOption.NEXT_CLICK || buttonOption == FooterButtonOption.SKIP_CLICK) {
      this.reportService.updateMaxStage(PageNumbers.MANAGER_SALARY);
      this.router.navigateByUrl(`/report/${this.currentReport.ID}/adjustments`);
    } else if (buttonOption == FooterButtonOption.BACK_CLICK) {
      if (this.reportService.showRentAdjustments()) {
        this.router.navigateByUrl(`/report/${this.currentReport.ID}/rent/rent-details`);
      } else {
        // this.router.navigateByUrl(`/report/${this.currentReport.ID}/income/future-year`);
        /// this.router.navigateByUrl(`/report/${this.currentReport.ID}/income`);
        this.router.navigateByUrl(`/report/${this.currentReport.ID}/future-projection`);
      }
    }
  }

}
